import { ReactNode } from 'react';
import jwt_decode from 'jwt-decode';
import QueryString from 'qs';

import DupontLogger from '../DupontLogger';

const oAuthTokenURL = '/services/oauth2/token';

const TIME_TO_REFERESH_TOKEN_BEFORE_EXPIRY = 1; //minute

const Logger = DupontLogger('DupontSalesforceService');
export interface JWTDecodeOptions {
  name: string;
  exp: number;
  custom_attributes: any;
}

export interface SSOProviderProps {
  children?: ReactNode;
  loginUrl: string;
  authzServiceUrl: string;
  tokenServiceUrl: string;
  revokeServiceUrl: string;
  clientId: string;
  clientSecret: string;
  redirectUri: string;
  grant_type?: string;
  code?: string;
  code_verifier?: string;
}

export interface SSOAuthOptions {
  client_id: string;
  // client_secret: string;
  redirect_uri: string;
  grant_type: string;
}

export interface TokenData {
  refreshToken: string;
  authToken: string;
  userDetails: {
    Address?: string | null;
    UserCity?: string | null;
    UserCountry?: string | null;
    AppDetails?: string | null;
    City?: string | null;
    Zip_code?: string | null;
    UserEmail: string;
    PermissionSets?: string | null;
    Business_Segment?: string | null;
    Role?: string | null;
    First_Name?: string;
    Business_Industry?: string | null;
    Business_SubIndustry?: string | null;
    Job_Function?: string | null;
    UserPhone?: string | null;
    Last_Name: string;
    UserRegion?: string | null;
    State?: string | null;
    UserId: string | null;
    Company_Size?: string | null;
  };
  expiry: number;
  refreshInterval: number;
}



export const generateCodeVerifier = () => {
  const array = new Uint8Array(32);
  window.crypto.getRandomValues(array);
  return Array.from(array)
    .map(b => ('00' + b.toString(16)).slice(-2))
    .join('');
};

export const generateCodeChallenge = async (verifier: string) => {
  const encoder = new TextEncoder();
  const data = encoder.encode(verifier);
  const digest = await window.crypto.subtle.digest('SHA-256', data);
  const uint8Array = new Uint8Array(digest);

  let binary = '';
  for (let i = 0; i < uint8Array.length; i++) {
    binary += String.fromCharCode(uint8Array[i]);
  }

  return btoa(binary)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
};

const getSSOToken = async (params: SSOProviderProps) => {
  const { loginUrl, code, clientId, redirectUri, grant_type } = params;

  const tokenParams = {
    code,
    grant_type,
    client_id: clientId,
    redirect_uri: redirectUri,
    code_verifier: params.code_verifier,
  };

  const queryParams = QueryString.stringify(tokenParams);
  const URL = `${loginUrl}${oAuthTokenURL}?${queryParams}`;
  try {
    const response =
      await fetch(`${loginUrl}${oAuthTokenURL}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        },
        body: QueryString.stringify(tokenParams)
      });
    // await fetch(URL, { method: 'POST', body: null });
    if (response.ok) {
      const responseJSON = await response.json();
      return responseJSON;
    } else {
      throw new Error('Network response was not ok');
    }
  } catch (error) {
    Logger.error('Salesforce Error:', error);
  }
};

const getRefreshInterval = (expiry: number) => expiry - TIME_TO_REFERESH_TOKEN_BEFORE_EXPIRY * 60;

export const getSalesforceToken = async (options: any): Promise<TokenData | undefined> => {
  const queryParams = new URLSearchParams(window.location.search);
  const code = queryParams?.get('code') || '';
  const params = { ...options, code };
  const response = await getSSOToken(params);
  if (response && !response.error) {
    const { refresh_token, id_token } = response;
    const decodedAccessToken = jwt_decode<JWTDecodeOptions>(id_token);
    const { custom_attributes, exp } = decodedAccessToken;
    return {
      refreshToken: refresh_token,
      authToken: id_token,
      userDetails: custom_attributes,
      expiry: exp,
      refreshInterval: getRefreshInterval(exp),
    };
  }
};

export const parseToken = (token: string) => {
  const decodedAccessToken = jwt_decode<JWTDecodeOptions>(token);
  const { custom_attributes, exp } = decodedAccessToken;
  return {
    refreshToken: '0',
    authToken: token,
    userDetails: custom_attributes,
    expiry: exp,
    refreshInterval: getRefreshInterval(exp),
  };
};
